import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OnboardingLayout from '../../components/onboarding/OnboardingLayout';
import OptionButton from '../../components/onboarding/OptionButton';
import { useOnboarding } from '../../contexts/OnboardingContext';

const OnboardingStep4 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { responses, updateResponses, markStepCompleted } = useOnboarding();
  const [housingType, setHousingType] = useState<'own' | 'rent' | 'parents' | null>(
    responses.housingType
  );
  const [hasKPR, setHasKPR] = useState<boolean | null>(responses.hasKPR);
  const [showKPRQuestion, setShowKPRQuestion] = useState(false);
  const progress = (4 / 14) * 100;

  const housingOptions = [
    { value: 'own' as const, label: t('onboarding.step4.housing_options.own') },
    { value: 'rent' as const, label: t('onboarding.step4.housing_options.rent') },
    { value: 'parents' as const, label: t('onboarding.step4.housing_options.parents') },
  ];

  const kprOptions = [
    { value: true, label: t('onboarding.step4.kpr_options.true') },
    { value: false, label: t('onboarding.step4.kpr_options.false') },
  ];

  useEffect(() => {
    if (housingType === 'own') {
      setShowKPRQuestion(true);
    } else {
      setShowKPRQuestion(false);
      setHasKPR(null);
    }
  }, [housingType]);

  const canProceed = housingType !== null && (housingType !== 'own' || hasKPR !== null);

  const handleNext = () => {
    updateResponses('housingType', housingType);
    updateResponses('hasKPR', hasKPR);
    markStepCompleted(4);
    navigate('/onboarding/step-5');
  };

  return (
    <OnboardingLayout progress={progress}>
      <div className="bg-white border-4 border-[#077f5d] p-8" style={{ boxShadow: '6px 6px 0 #077f5d' }}>
        <div className="mb-6">
          <h2
            className="text-center text-[#077f5d] mb-4 uppercase"
            style={{ fontFamily: "'Press Start 2P'", lineHeight: '1.3' }}
          >
            {t('onboarding.step4.title')}
          </h2>
        </div>

        <div className="space-y-3 mb-6">
          {housingOptions.map(option => (
            <OptionButton
              key={option.value}
              label={option.label}
              selected={housingType === option.value}
              onClick={() => setHousingType(option.value)}
            />
          ))}
        </div>

        {showKPRQuestion && (
          <div className="mb-6 pt-4 border-t-2 border-black">
            <p className="text-fittory-elf font-mono mb-4 font-bold font-mono text-center text-sm">
              {t('onboarding.step4.kpr_question')}
            </p>
            <div className="space-y-3">
              {kprOptions.map(option => (
                <OptionButton
                  key={option.label}
                  label={option.label}
                  selected={hasKPR === option.value}
                  onClick={() => setHasKPR(option.value)}
                />
              ))}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={handleNext}
          disabled={!canProceed}
          className="w-full bg-[#3cbe8c] text-white px-8 py-4 border-2 border-[#077f5d] font-bold uppercase   hover:-translate-y-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-75 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0"
          style={{
            fontFamily: "'Press Start 2P', monospace",
            fontSize: '12px',
            boxShadow: '6px 6px 0 #077f5d'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
            e.currentTarget.style.transform = 'translate(-2px, -2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
            e.currentTarget.style.transform = 'translate(0, 0)';
          }}
        >
          {t('onboarding.step4.next')}
        </button>
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingStep4;
